import {
  AddUniversal, CloseUniversal, PlaybackSpeedUniversal, TickMarkOutlined, TimerUniversal,
  UndoUniversal, WarningOutlined,
} from '@duxton-web/icons';
import {
  Alert, Button, Card, Empty, Flex, Input, Modal, Select, Table, Tabs, Tag,
  Switch, Tooltip, Typography,
} from '@duxton-web/ui';
import i18n from 'i18n-js';
import { AudienceSourceType } from 'omega-rtc/types/ts/content-config';
import React, { useEffect, useRef, useState } from 'react';

import {
  MAX_AUDIENCE_REASON_LENGTH, PRODUCT_LANG_FIELDS, audienceReasonLength,
  formatDateTime, isAudienceReasonValid, langLabel as LANG_LABEL, localeSorter,
  nameFromEmail, normalizeAudienceReason, normalizeProductAudienceAssociations,
  productDisplayName,
} from './adapters';
import DiffField, { DiffChips } from './DiffField';
import {
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitProductForApproval,
} from './permissions';
import { contentConfigModalStyle } from './theme';

const { TextArea } = Input;
const AUDIENCE_LOCALE = 'ucd_content_config.product_offerings.audiences';
const audienceText = (key, options = {}) => i18n.t(`${AUDIENCE_LOCALE}.${key}`, options);

const STATUS_TAG = {
  active: { color: 'success', label: 'ACTIVE' },
  pending: { color: 'processing', label: 'PENDING APPROVAL' },
  draft: { color: 'warning', label: 'DRAFT' },
};

export const audienceAssociationTagColor = (option) => {
  const regional = !option || !option.country;
  if (option && option.sourceType === AudienceSourceType.Table) {
    return regional ? 'purple' : 'magenta';
  }
  return regional ? 'cyan' : 'blue';
};

export const FieldLabel = ({ children, required }) => (
  <Typography.Body level={2} strong className="cc-field-label">
    {children}
    {required ? ' *' : ''}
  </Typography.Body>
);

const CtaInput = ({ value, onChange, disabled }) => {
  const items = (value || '').split('\n').map((s) => s.trim()).filter(Boolean);
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v].join('\n'));
    setDraft('');
  };
  return (
    <div>
      {items.length > 0 && (
        <Flex gap={6} wrap="wrap" className={disabled ? '' : 'cc-cta-list'}>
          {items.map((c, i) => (
            <Tag key={`${c}-${i}`} closable={!disabled} onClose={() => onChange(items.filter((_, j) => j !== i).join('\n'))}>{c}</Tag>
          ))}
        </Flex>
      )}
      {!disabled && (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={add}
          placeholder="Type a CTA and press Enter"
          suffix={<Button type="text" size="small" shape="circle" icon={<AddUniversal />} onClick={add} />}
        />
      )}
    </div>
  );
};

export const ProductOfferingList = ({
  canCreate,
  country,
  countries,
  listTab,
  loading,
  onCountryChange,
  onCreate,
  onOpenDetail,
  rows,
  setListTab,
  setVerticalFilter,
  verticalFilter,
  verticalName,
  verticals,
}) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: localeSorter('name'),
      render: (name, row) => (
        <Flex align="center" gap={6}>
          <Typography.Body level={2}>{name}</Typography.Body>
          {row.status === 'pending' && (
            <Tooltip title="Pending approval">
              <TimerUniversal className="cc-pending-icon" />
            </Tooltip>
          )}
          {row.status === 'draft' && <Tag color="warning">DRAFT</Tag>}
        </Flex>
      ),
    },
    {
      title: 'Vertical',
      dataIndex: 'vertical',
      render: (v) => <Typography.Body level={2}>{verticalName(v)}</Typography.Body>,
    },
    {
      title: 'CI Languages',
      dataIndex: 'ciEnabledLangs',
      width: 220,
      render: (enabledLangs) => ((enabledLangs || []).length ? (
        <Flex gap={4} wrap="wrap">
          {(enabledLangs || []).map((lang) => <Tag color="success" key={lang}>{LANG_LABEL(lang)}</Tag>)}
        </Flex>
      ) : (
        <Tag>NONE</Tag>
      )),
    },
    {
      title: 'Updated by',
      dataIndex: 'updatedBy',
      render: (u) => <Typography.Body level={2} type="secondary">{nameFromEmail(u) || '—'}</Typography.Body>,
    },
    {
      title: 'Updated on',
      dataIndex: 'updatedAt',
      width: 200,
      render: (t) => <Typography.Body level={2} type="secondary">{formatDateTime(t)}</Typography.Body>,
    },
  ];

  return (
    <Flex vertical gap="middle">
      <Flex align="center" gap="middle" wrap="wrap">
        <Typography.Title level={4} className="cc-title-reset">Product Offerings</Typography.Title>
        <Tooltip title={canCreate ? '' : "You don't have permission to create product offerings."}>
          <Button type="primary" icon={<AddUniversal />} disabled={!canCreate} onClick={onCreate}>
            Create
          </Button>
        </Tooltip>
      </Flex>

      <Tabs
        type="pill"
        activeKey={listTab}
        onChange={setListTab}
        items={[
          { key: 'all', label: 'All' },
          { key: 'pending', label: 'Pending Approval' },
          { key: 'ci-enabled', label: 'Content Injection' },
        ]}
      />

      <Flex align="center" gap={8} wrap="wrap" className="cc-list-controls">
        <Typography.Body level={2} type="secondary">Country</Typography.Body>
        <Select
          value={country}
          onChange={onCountryChange}
          className="cc-country-select"
          options={countries.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Typography.Body level={2} type="secondary">Vertical</Typography.Body>
        <Select
          value={verticalFilter}
          onChange={setVerticalFilter}
          className="cc-vertical-select"
          options={[
            { value: '', label: 'All' },
            ...verticals.map((v) => ({ value: v.id, label: v.name })),
          ]}
        />
      </Flex>

      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        onRow={(r) => ({ onClick: () => onOpenDetail(r.key), className: 'cc-row-clickable' })}
        locale={{
          emptyText: {
            pending: 'No product offerings pending approval in this country.',
            'ci-enabled': 'No CI enabled product offerings in this country.',
          }[listTab] || 'No product offerings in this country yet.',
        }}
      />
    </Flex>
  );
};

export const AudienceConfigurationModal = ({
  disabled = false,
  failed = false,
  loaded = false,
  loading = false,
  onApply,
  onClose,
  onRetry,
  open,
  options = [],
  value = [],
}) => {
  const [draftAssociations, setDraftAssociations] = useState([]);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraftAssociations(normalizeProductAudienceAssociations(value));
    }
    wasOpen.current = open;
  }, [open, value]);

  const selectedKeys = draftAssociations.map(({ audienceKey }) => audienceKey);
  const optionByKey = new Map(options.map((option) => [option.key, option]));
  const visibleOptions = options.filter(
    (option) => option.eligible || selectedKeys.includes(option.key),
  );
  const eligibleCount = options.filter((option) => option.eligible).length;
  const hasInvalidReferences = loaded && draftAssociations.some(({ audienceKey }) => {
    const option = optionByKey.get(audienceKey);
    return !option || !option.eligible;
  });
  const hasInvalidReasons = draftAssociations.some(
    ({ reasonToUse }) => !isAudienceReasonValid(reasonToUse),
  );

  const setAudienceKeys = (keys) => {
    const associationByKey = new Map(
      draftAssociations.map((association) => [association.audienceKey, association]),
    );
    setDraftAssociations(keys.map((audienceKey) => (
      associationByKey.get(audienceKey) || { audienceKey, reasonToUse: '' }
    )));
  };
  const setAudienceReason = (audienceKey, reasonToUse) => {
    setDraftAssociations(draftAssociations.map((association) => (
      association.audienceKey === audienceKey
        ? { ...association, reasonToUse }
        : association
    )));
  };
  const apply = () => {
    if (hasInvalidReferences || hasInvalidReasons) return;
    onApply(draftAssociations);
  };

  return (
    <Modal
      title={audienceText('details')}
      open={open}
      onCancel={onClose}
      width={1200}
      style={contentConfigModalStyle}
      className="cc-modal cc-audience-modal"
      footer={(
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>
            {disabled ? audienceText('close') : audienceText('cancel')}
          </Button>
          {!disabled && (
            <Button
              type="primary"
              disabled={hasInvalidReferences || hasInvalidReasons}
              onClick={apply}
            >
              {audienceText('apply')}
            </Button>
          )}
        </Flex>
      )}
    >
      <Flex vertical gap="middle">
        {loading && (
          <Typography.Body level={2} type="secondary">
            {audienceText('loading')}
          </Typography.Body>
        )}
        {failed && (
          <Alert
            type="error"
            showIcon
            message={(
              <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                <span>{audienceText('error')}</span>
                <Button size="small" onClick={onRetry}>
                  {audienceText('retry')}
                </Button>
              </Flex>
            )}
          />
        )}
        {loaded && eligibleCount === 0 && (
          <Typography.Body level={2} type="secondary">
            {audienceText('empty')}
          </Typography.Body>
        )}

        <div>
          <FieldLabel>{audienceText('selection_label')}</FieldLabel>
          <Select
            mode="multiple"
            value={selectedKeys}
            onChange={setAudienceKeys}
            options={visibleOptions.map((option) => ({
              value: option.key,
              label: option.eligible
                ? option.label
                : `${option.label} · ${audienceText('unavailable')}`,
            }))}
            placeholder={audienceText('add_placeholder')}
            allowClear
            loading={loading}
            disabled={disabled || loading || failed || !loaded}
            className="cc-full-width"
            optionFilterProp="label"
            optionRender={(selectOption) => {
              const option = optionByKey.get(String(selectOption.value));
              if (!option) return selectOption.label;
              const fullLabel = `${option.label}${option.eligible
                ? ''
                : ` · ${audienceText('unavailable')}`}`;
              return (
                <Tag
                  color={option.eligible
                    ? audienceAssociationTagColor(option)
                    : 'error'}
                  className="cc-audience-dropdown-option"
                >
                  <span className="cc-audience-dropdown-option-label">
                    {fullLabel}
                  </span>
                </Tag>
              );
            }}
            tagRender={({
              label, closable, onClose: onTagClose, value: optionValue,
            }) => {
              const option = optionByKey.get(String(optionValue));
              const fullLabel = option
                ? `${option.label}${option.eligible ? '' : ` · ${audienceText('unavailable')}`}`
                : String(label);
              const chipLabel = option
                ? option.name || option.key
                : String(label);
              return (
                <Tooltip
                  title={fullLabel}
                  key={String(optionValue)}
                >
                  <Tag
                    color={option && !option.eligible
                      ? 'error'
                      : audienceAssociationTagColor(option)}
                    closable={closable}
                    onClose={onTagClose}
                    className="cc-tag-gap cc-audience-selection-tag"
                  >
                    <span className="cc-audience-selection-tag-label">{chipLabel}</span>
                  </Tag>
                </Tooltip>
              );
            }}
          />
        </div>

        {draftAssociations.length === 0 && (
          <Empty description={audienceText('no_selection')} />
        )}
        {draftAssociations.length > 0 && (
          <div className="cc-audience-association-grid">
            {draftAssociations.map(({ audienceKey, reasonToUse }) => {
              const option = optionByKey.get(audienceKey);
              const displayLabel = option
                ? option.label
                : `${audienceText('unavailable_prefix')} · ${audienceKey}`;
              const reasonLength = audienceReasonLength(reasonToUse);
              const reasonValid = isAudienceReasonValid(reasonToUse);
              return (
                <div
                  className={`cc-audience-association${option && !option.eligible ? ' cc-audience-association--invalid' : ''}`}
                  key={audienceKey}
                >
                  <Flex align="center" gap={6} wrap="wrap">
                    <Tooltip title={displayLabel}>
                      <Tag
                        color={option && option.eligible
                          ? audienceAssociationTagColor(option)
                          : 'error'}
                        className="cc-audience-association-heading"
                      >
                        <span className="cc-audience-association-heading-label">
                          {displayLabel}
                        </span>
                      </Tag>
                    </Tooltip>
                    {option && !option.eligible && (
                      <Tag color="error">{audienceText('unavailable')}</Tag>
                    )}
                  </Flex>
                  {option && !option.eligible && loaded && (
                    <Typography.Body level={2} type="danger">
                      {audienceText('invalid_reference')}
                    </Typography.Body>
                  )}
                  <div>
                    <Typography.Body
                      level={3}
                      strong
                      className="cc-audience-reason-label"
                    >
                      {audienceText('reason_label')}
                    </Typography.Body>
                    <TextArea
                      aria-label={`${audienceText('reason_label')}: ${displayLabel}`}
                      value={reasonToUse}
                      onChange={(event) => setAudienceReason(audienceKey, event.target.value)}
                      placeholder={audienceText('reason_help')}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      disabled={disabled}
                      status={reasonValid ? undefined : 'error'}
                    />
                    <Flex
                      justify={reasonValid ? 'flex-end' : 'space-between'}
                      align="start"
                      gap={8}
                    >
                      {!reasonValid && (
                        <Typography.Metadata type="danger">
                          {audienceText('reason_too_long', {
                            max: MAX_AUDIENCE_REASON_LENGTH,
                          })}
                        </Typography.Metadata>
                      )}
                      <Typography.Metadata type={reasonValid ? 'secondary' : 'danger'}>
                        {reasonLength}/{MAX_AUDIENCE_REASON_LENGTH}
                      </Typography.Metadata>
                    </Flex>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Flex>
    </Modal>
  );
};

const ProductLinks = ({
  attrOptions,
  audienceListFailed = false,
  audienceListLoaded = false,
  audienceListLoading = false,
  audienceOptions = [],
  audiencesEnabled = false,
  cvpOptions,
  disabled,
  listVal,
  onRetryAudiences,
  personaOptions,
  setList,
}) => {
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const linkCard = ({
    title, color, field, options,
  }) => {
    const selected = listVal(field);
    return (
      <Card bordered={false}>
        <Flex vertical gap="small">
          <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
            <Typography.Body level={2} strong className="cc-title-reset">{title}</Typography.Body>
            <Tag>{selected.length} of {options.length}</Tag>
          </Flex>
          <Select
            mode="multiple"
            value={selected}
            onChange={(vals) => setList(field, vals)}
            options={options.map((o) => ({ value: o.key, label: o.key }))}
            placeholder="Add..."
            allowClear
            disabled={disabled}
            className="cc-full-width"
            optionFilterProp="label"
            tagRender={({ label, closable, onClose }) => (
              <Tag color={color} closable={closable} onClose={onClose} className="cc-tag-gap">
                {label}
              </Tag>
            )}
          />
        </Flex>
      </Card>
    );
  };

  const audienceAssociations = normalizeProductAudienceAssociations(listVal('audiences'));
  const audienceOptionByKey = new Map(audienceOptions.map((option) => [option.key, option]));

  return (
    <Flex vertical gap="middle">
      {linkCard({
        title: 'CVP', color: 'volcano', field: 'cvps', options: cvpOptions,
      })}
      {!audiencesEnabled && linkCard({
        title: 'Attribute', color: 'blue', field: 'attrs', options: attrOptions,
      })}
      {linkCard({
        title: 'Persona', color: 'purple', field: 'personas', options: personaOptions,
      })}
      {audiencesEnabled && (
        <Card bordered={false}>
          <Flex vertical gap="small">
            <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
              <Flex align="center" gap={8}>
                <Typography.Body level={2} strong className="cc-title-reset">
                  {audienceText('title')}
                </Typography.Body>
                <Tag>{audienceAssociations.length}</Tag>
              </Flex>
              <Button size="small" onClick={() => setAudienceModalOpen(true)}>
                {audienceText('details')}
              </Button>
            </Flex>
            {audienceAssociations.length === 0 ? (
              <Typography.Body level={2} type="secondary">
                {audienceText('not_configured')}
              </Typography.Body>
            ) : (
              <Flex gap={6} wrap="wrap" className="cc-audience-chip-list">
                {audienceAssociations.map(({ audienceKey }) => {
                  const option = audienceOptionByKey.get(audienceKey);
                  const fullLabel = option
                    ? `${option.label}${option.eligible
                      ? ''
                      : ` · ${audienceText('unavailable')}`}`
                    : `${audienceText('unavailable_prefix')} · ${audienceKey}`;
                  const chipLabel = option
                    ? option.name || option.key
                    : audienceKey;
                  return (
                    <Tooltip title={fullLabel} key={audienceKey}>
                      <Tag
                        color={option && option.eligible
                          ? audienceAssociationTagColor(option)
                          : 'error'}
                        className="cc-audience-summary-chip"
                      >
                        <span className="cc-audience-summary-chip-label">
                          {chipLabel}
                        </span>
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Flex>
            )}
          </Flex>
          <AudienceConfigurationModal
            disabled={disabled}
            failed={audienceListFailed}
            loaded={audienceListLoaded}
            loading={audienceListLoading}
            onApply={(associations) => {
              setList('audiences', associations);
              setAudienceModalOpen(false);
            }}
            onClose={() => setAudienceModalOpen(false)}
            onRetry={onRetryAudiences}
            open={audienceModalOpen}
            options={audienceOptions}
            value={audienceAssociations}
          />
        </Card>
      )}
    </Flex>
  );
};

const ProductLanguageTabs = ({
  activeLang,
  disabled,
  langVal,
  langs,
  setActiveLang,
  setLang,
}) => {
  const hasRequiredContent = (code) => Boolean(
    String(langVal(code, 'name') || '').trim()
    && String(langVal(code, 'overview') || '').trim(),
  );
  const isCIEnabled = (code) => hasRequiredContent(code) && Boolean(langVal(code, 'ciEnabled'));

  return (
    <Card bordered={false} className="cc-section-gap">
      <Tabs
        activeKey={activeLang}
        onChange={setActiveLang}
        type="pill"
        tabBarExtraContent={{
          right: <Tag>{langs.filter((c) => langVal(c, 'name').trim()).length} of {langs.length} filled</Tag>,
        }}
        items={langs.map((code) => ({
          key: code,
          label: (
            <Flex align="center" gap={4}>
              <span>{LANG_LABEL(code)}</span>
              {langVal(code, 'name').trim() ? <TickMarkOutlined /> : null}
            </Flex>
          ),
          children: (
            <Flex vertical gap={16}>
              <div className="cc-lang-name-row">
                <div>
                  <FieldLabel required>Name</FieldLabel>
                  <Input
                    value={langVal(code, 'name')}
                    onChange={(e) => setLang(code, 'name', e.target.value)}
                    placeholder={`Localized product name in ${LANG_LABEL(code)}...`}
                    disabled={disabled}
                  />
                </div>
                <div className="cc-lang-ci-field">
                  <FieldLabel>CI Enabled</FieldLabel>
                  <Flex align="center" gap={8} className="cc-lang-ci-toggle">
                    <Switch
                      checked={isCIEnabled(code)}
                      onChange={(checked) => setLang(code, 'ciEnabled', hasRequiredContent(code) && checked)}
                      disabled={disabled || !hasRequiredContent(code)}
                    />
                    <Typography.Body level={2} type="secondary">
                      {isCIEnabled(code) ? 'Enabled' : 'Disabled'}
                    </Typography.Body>
                  </Flex>
                </div>
              </div>
              <div>
                <FieldLabel required>Product overview</FieldLabel>
                <TextArea value={langVal(code, 'overview')} onChange={(e) => setLang(code, 'overview', e.target.value)} autoSize={{ minRows: 3, maxRows: 10 }} disabled={disabled} />
              </div>
              <div>
                <FieldLabel>Reason to use</FieldLabel>
                <TextArea value={langVal(code, 'reason')} onChange={(e) => setLang(code, 'reason', e.target.value)} autoSize={{ minRows: 3, maxRows: 12 }} disabled={disabled} />
              </div>
              <div>
                <FieldLabel>Call to action</FieldLabel>
                <CtaInput value={langVal(code, 'cta')} onChange={(v) => setLang(code, 'cta', v)} disabled={disabled} />
              </div>
            </Flex>
          ),
        }))}
      />
    </Card>
  );
};

const ProductDiffModal = ({
  audienceLabelForKey = (key) => key,
  audiencesEnabled = false,
  busy,
  canReview,
  canUpdate,
  comparisonDiff,
  generationDisabledReason,
  isMine,
  langs,
  onApprove,
  onCancel,
  onGenerate,
  onRecall,
  onReject,
  open,
  sel,
  verticalName,
}) => {
  const isDraftDiff = Boolean(sel && sel.draft && comparisonDiff && !sel.active && !sel.pending);
  const isLocalDiff = Boolean(sel && sel.active && comparisonDiff && !sel.pending);
  const a = isDraftDiff
    ? {
      name: '',
      vertical: '',
      byLang: {},
      whitelist: {
        cvps: [], attrs: [], personas: [], audiences: [],
      },
    }
    : sel && sel.active;
  const p = isDraftDiff || isLocalDiff ? comparisonDiff : sel && sel.pending;
  const activeLabel = isDraftDiff ? 'CURRENT' : 'ACTIVE';
  const activeTagColor = isDraftDiff ? undefined : 'error';
  const pendingTagColor = isDraftDiff || isLocalDiff ? 'warning' : 'success';
  let pendingLabel = 'PENDING APPROVAL';
  let title = 'Pending approval vs current';
  if (isDraftDiff) {
    pendingLabel = 'DRAFT';
    title = 'Draft vs current';
  } else if (isLocalDiff) {
    pendingLabel = 'LOCAL EDITS';
    title = 'Local edits vs current';
  }
  const canRecallChange = canRecallPendingChange({ canUpdate }, isMine);
  const canReviewChange = canReviewPendingChange({ canReview }, isMine);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={sel && sel.pending ? (
        <Flex justify="flex-end" gap={8}>
          <Tooltip title={generationDisabledReason}>
            <Button
              size="small"
              icon={<PlaybackSpeedUniversal />}
              disabled={Boolean(generationDisabledReason)}
              onClick={() => { onCancel(); onGenerate(); }}
            >
              Generate content
            </Button>
          </Tooltip>
          {canRecallChange ? (
            <Button size="small" loading={busy} onClick={() => { onCancel(); onRecall(); }}>Recall</Button>
          ) : null}
          {canReviewChange ? (
            <React.Fragment>
              <Button size="small" type="primary" danger icon={<CloseUniversal />} loading={busy} onClick={() => { onCancel(); onReject(); }}>Reject</Button>
              <Button size="small" type="primary" icon={<TickMarkOutlined />} loading={busy} onClick={() => { onCancel(); onApprove(); }}>Approve</Button>
            </React.Fragment>
          ) : null}
        </Flex>
      ) : null}
      width={1472}
      style={contentConfigModalStyle}
      className="cc-modal"
    >
      {a && p && (() => {
        const lv = (rec, lang, f) => {
          const byLang = (((rec && rec.byLang) || {})[lang] || {});
          const raw = byLang[f.key];
          if (f.type === 'boolean') {
            return raw && byLang.name && byLang.name.trim() && byLang.overview && byLang.overview.trim()
              ? 'Enabled'
              : 'Disabled';
          }
          return raw || '';
        };
        const langChanged = (lang) => PRODUCT_LANG_FIELDS.some((f) => lv(p, lang, f) !== lv(a, lang, f));
        const activeAudiences = normalizeProductAudienceAssociations(
          (a.whitelist && a.whitelist.audiences) || [],
        );
        const pendingAudiences = normalizeProductAudienceAssociations(
          (p.whitelist && p.whitelist.audiences) || [],
        );
        const activeAudienceByKey = new Map(
          activeAudiences.map((association) => [association.audienceKey, association]),
        );
        const changedAudienceReasons = pendingAudiences.filter((association) => {
          const activeAssociation = activeAudienceByKey.get(association.audienceKey);
          const activeReason = normalizeAudienceReason(
            activeAssociation ? activeAssociation.reasonToUse : '',
          );
          const pendingReason = normalizeAudienceReason(association.reasonToUse);
          return activeReason !== pendingReason;
        });
        return (
          <div className="cc-diff">
            <DiffField
              label="Tagged Name"
              activeLabel={activeLabel}
              activeTagColor={activeTagColor}
              pendingLabel={pendingLabel}
              pendingTagColor={pendingTagColor}
              activeVal={a.name || ''}
              pendingVal={p.name || ''}
            />
            <DiffField
              label="Vertical"
              activeLabel={activeLabel}
              activeTagColor={activeTagColor}
              pendingLabel={pendingLabel}
              pendingTagColor={pendingTagColor}
              activeVal={verticalName(a.vertical)}
              pendingVal={verticalName(p.vertical)}
            />
            <div className="cc-diff-language-section">
              <Tabs
                type="pill"
                items={langs.map((lang) => ({
                  key: lang,
                  label: (
                    <Flex align="center" gap={4}>
                      {langChanged(lang) ? (
                        <WarningOutlined className="cc-diff-language-warning" />
                      ) : null}
                      <span>{LANG_LABEL(lang)}</span>
                    </Flex>
                  ),
                  children: (
                    <div>
                      {PRODUCT_LANG_FIELDS.map((f) => (
                        <DiffField
                          key={f.key}
                          label={f.label}
                          activeLabel={activeLabel}
                          activeTagColor={activeTagColor}
                          pendingLabel={pendingLabel}
                          pendingTagColor={pendingTagColor}
                          mode={f.mode || 'word'}
                          activeVal={lv(a, lang, f)}
                          pendingVal={lv(p, lang, f)}
                        />
                      ))}
                    </div>
                  ),
                }))}
              />
            </div>
            <DiffChips label="CVP links" active={a.whitelist.cvps || []} pending={p.whitelist.cvps || []} />
            <DiffChips label="Attribute links" active={a.whitelist.attrs || []} pending={p.whitelist.attrs || []} />
            <DiffChips label="Persona links" active={a.whitelist.personas || []} pending={p.whitelist.personas || []} />
            {audiencesEnabled && (
              <React.Fragment>
                <DiffChips
                  label={audienceText('diff_label')}
                  active={activeAudiences.map(({ audienceKey }) => audienceKey)}
                  pending={pendingAudiences.map(({ audienceKey }) => audienceKey)}
                  labelForValue={audienceLabelForKey}
                />
                {changedAudienceReasons.map((association) => {
                  const activeAssociation = activeAudienceByKey.get(association.audienceKey);
                  return (
                    <DiffField
                      key={`audience-reason-${association.audienceKey}`}
                      label={audienceText('reason_diff_label', {
                        audience: audienceLabelForKey(association.audienceKey),
                      })}
                      activeLabel={activeLabel}
                      activeTagColor={activeTagColor}
                      pendingLabel={pendingLabel}
                      pendingTagColor={pendingTagColor}
                      activeVal={normalizeAudienceReason(
                        activeAssociation ? activeAssociation.reasonToUse : '',
                      )}
                      pendingVal={normalizeAudienceReason(association.reasonToUse)}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </div>
        );
      })()}
    </Modal>
  );
};

export const ProductOfferingDetail = ({
  activeLang,
  attrOptions,
  audienceLabelForKey = (key) => key,
  audienceListFailed = false,
  audienceListLoaded = false,
  audienceListLoading = false,
  audienceOptions = [],
  audienceValidationUnavailable = false,
  audiencesEnabled = false,
  base,
  busy,
  canCreate,
  canReview,
  canUpdate,
  country,
  countryName,
  cvpOptions,
  defaultLang,
  discardDraft,
  discardLocalEdits,
  draftReady,
  hasEdits,
  hasInvalidAudienceReasons = false,
  hasInvalidAudiences = false,
  hasPending,
  isDraft,
  isMine,
  generationDisabledReason,
  langVal,
  langs,
  listVal,
  nameVal,
  onApprove,
  onGenerate,
  onRecall,
  onReject,
  onRetryAudiences,
  onSendForApproval,
  pendingAuthor,
  pendingExpiry,
  personaOptions,
  productNameReady,
  sel,
  setActiveLang,
  setLang,
  setList,
  setNameEdit,
  setShowDiff,
  setShowPending,
  setVerticalEdit,
  showDiff,
  showPending,
  verticalName,
  verticals,
  verticalVal,
}) => {
  if (!sel || !base) {
    return (
      <Empty description={`Add a Product Offering to start editing Mystique inputs for ${country}.`} />
    );
  }

  let chip = { tone: 'active', label: 'ACTIVE' };
  if (isDraft) chip = { tone: 'draft', label: 'DRAFT' };
  else if (hasPending) chip = { tone: 'pending', label: 'PENDING APPROVAL' };
  else if (hasEdits) chip = { tone: 'draft', label: 'LOCAL EDITS' };

  const canEdit = isDraft ? canCreate : canUpdate;
  const canSubmitForApproval = productNameReady && canSubmitProductForApproval(
    { canCreate, canUpdate },
    {
      isDraft,
      draftReady,
      hasActive: Boolean(sel.active),
      hasEdits,
      hasPending,
      hasInvalidAudiences,
      hasInvalidAudienceReasons,
    },
  );
  const canRecallChange = canRecallPendingChange({ canUpdate }, isMine);
  const canReviewChange = canReviewPendingChange({ canReview }, isMine);

  let sendTip = '';
  if (!canEdit) {
    sendTip = isDraft
      ? "You don't have permission to create product offerings."
      : "You don't have permission to update product offerings.";
  } else if (hasPending) sendTip = 'A pending change already exists - resolve it first.';
  else if (!productNameReady) sendTip = 'Enter the tagged name.';
  else if (isDraft && !draftReady) sendTip = `Enter the ${LANG_LABEL(defaultLang)} name & product overview`;
  else if (!isDraft && !sel.active) sendTip = 'This offering has no active version to update.';
  else if (audienceValidationUnavailable) sendTip = audienceText('validation_unavailable');
  else if (hasInvalidAudiences) sendTip = audienceText('invalid_submission');
  else if (hasInvalidAudienceReasons) {
    sendTip = audienceText('reason_too_long', { max: MAX_AUDIENCE_REASON_LENGTH });
  }

  const canShowDiff = (sel.active && sel.pending) || isDraft || (hasEdits && sel.active && !hasPending);
  const comparisonByLang = {};
  if (isDraft || (hasEdits && sel.active && !hasPending)) {
    langs.forEach((lang) => {
      const name = langVal(lang, 'name');
      const overview = langVal(lang, 'overview');
      comparisonByLang[lang] = {
        name,
        ciEnabled: Boolean(langVal(lang, 'ciEnabled')) && Boolean(name.trim() && overview.trim()),
        overview,
        reason: langVal(lang, 'reason'),
        cta: langVal(lang, 'cta'),
      };
    });
  }
  const comparisonDiff = isDraft || (hasEdits && sel.active && !hasPending) ? {
    name: nameVal(),
    vertical: verticalVal(),
    byLang: comparisonByLang,
    whitelist: {
      cvps: listVal('cvps'),
      attrs: listVal('attrs'),
      personas: listVal('personas'),
      audiences: listVal('audiences'),
    },
  } : null;

  return (
    <div>
      {hasPending && sel.active && (
        <Alert
          type="info"
          showIcon
          className="cc-action-bar"
          message={(
            <Flex justify="space-between" align="center" gap={12} wrap="wrap">
              <div className="cc-alert-content">
                <div>
                  Showing <strong>{showPending ? 'PENDING APPROVAL' : 'ACTIVE'}</strong> version.
                  {' '}Config has new changes pending approval and can&apos;t be changed at the moment.
                </div>
                <div>
                  Changes were made by <strong>{pendingAuthor || '—'}</strong>
                  {pendingExpiry
                    ? (
                      <React.Fragment>
                        {' '}and will expire at <strong>{pendingExpiry}</strong>
                      </React.Fragment>
                    )
                    : null}.
                </div>
              </div>
              <Button size="small" onClick={() => setShowPending((v) => !v)}>
                {showPending ? 'See active' : 'See changes'}
              </Button>
            </Flex>
          )}
        />
      )}

      <Flex vertical gap={4} className="cc-detail-header">
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Title level={4} className="cc-title-reset">{nameVal() || productDisplayName(base, activeLang)}</Typography.Title>
          <Tag>{countryName(country)}</Tag>
          <Select
            value={verticalVal()}
            onChange={setVerticalEdit}
            disabled={hasPending || !canEdit}
            className="cc-vertical-select"
            options={verticals.map((v) => ({ value: v.id, label: v.name }))}
          />
        </Flex>
        <Typography.Metadata type="secondary">{isDraft ? 'New draft - not saved yet' : `Updated by ${nameFromEmail(base.updatedBy || base.createdBy) || '—'}`}</Typography.Metadata>
      </Flex>

      <Flex align="center" justify="space-between" wrap="wrap" gap={12} className="cc-action-bar">
        <Flex align="center" gap={4}>
          <Tag color={STATUS_TAG[chip.tone].color}>{chip.label}</Tag>
          {hasPending && !sel.active && <Tag color="warning">NEW</Tag>}
        </Flex>
        <Flex gap={8} align="center" wrap="wrap">
          {canShowDiff && (
            <Button size="small" onClick={() => setShowDiff(true)}>Show diff</Button>
          )}
          <Tooltip title={generationDisabledReason}>
            <Button
              size="small"
              icon={<PlaybackSpeedUniversal />}
              disabled={Boolean(generationDisabledReason)}
              onClick={onGenerate}
            >
              Generate content
            </Button>
          </Tooltip>
          {isDraft && (
            <Button size="small" icon={<UndoUniversal />} danger onClick={discardDraft}>Discard draft</Button>
          )}
          {hasEdits && !hasPending && !isDraft && (
            <Button size="small" icon={<UndoUniversal />} danger onClick={discardLocalEdits}>Discard local edits</Button>
          )}
          {hasPending && canRecallChange && (
            <Button size="small" loading={busy} onClick={onRecall}>Recall</Button>
          )}
          {hasPending && canReviewChange && (
            <React.Fragment>
              <Button size="small" type="primary" danger icon={<CloseUniversal />} loading={busy} onClick={onReject}>Reject</Button>
              <Button size="small" type="primary" icon={<TickMarkOutlined />} loading={busy} onClick={onApprove}>Approve</Button>
            </React.Fragment>
          )}
          <Tooltip title={sendTip}>
            <Button
              size="small"
              type="primary"
              icon={<TickMarkOutlined />}
              loading={busy}
              disabled={!canSubmitForApproval}
              onClick={onSendForApproval}
            >
              Send for approval
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      <Card bordered={false} className="cc-section-gap">
        <FieldLabel required>Tagged Name</FieldLabel>
        <Input
          value={nameVal()}
          onChange={(e) => setNameEdit(e.target.value)}
          placeholder="Tagged name..."
          disabled={hasPending || !canEdit}
        />
      </Card>

      <ProductLanguageTabs
        activeLang={activeLang}
        disabled={hasPending || !canEdit}
        langVal={langVal}
        langs={langs}
        setActiveLang={setActiveLang}
        setLang={setLang}
      />

      <div className="cc-section-gap">
        <ProductLinks
          attrOptions={attrOptions}
          audienceListFailed={audienceListFailed}
          audienceListLoaded={audienceListLoaded}
          audienceListLoading={audienceListLoading}
          audienceOptions={audienceOptions}
          audiencesEnabled={audiencesEnabled}
          cvpOptions={cvpOptions}
          disabled={hasPending || !canEdit}
          listVal={listVal}
          onRetryAudiences={onRetryAudiences}
          personaOptions={personaOptions}
          setList={setList}
        />
      </div>

      <ProductDiffModal
        audienceLabelForKey={audienceLabelForKey}
        audiencesEnabled={audiencesEnabled}
        busy={busy}
        canReview={canReview}
        canUpdate={canUpdate}
        comparisonDiff={comparisonDiff}
        isMine={isMine}
        langs={langs}
        generationDisabledReason={generationDisabledReason}
        onApprove={onApprove}
        onCancel={() => setShowDiff(false)}
        onGenerate={onGenerate}
        onRecall={onRecall}
        onReject={onReject}
        open={showDiff}
        sel={sel}
        verticalName={verticalName}
      />
    </div>
  );
};

export const NewProductModal = ({
  canCreate, countries, defaultCountry, onClose, onCreate, open, verticals,
}) => {
  const [productCountry, setProductCountry] = useState('');
  const [vertical, setVertical] = useState('');
  const [name, setName] = useState('');
  useEffect(() => {
    if (open) {
      setProductCountry(defaultCountry || (countries[0] && countries[0].id) || '');
      setVertical((verticals[0] && verticals[0].id) || '');
      setName('');
    }
  }, [open, countries, defaultCountry, verticals]);

  const canContinue = canCreate && Boolean(productCountry && vertical && name.trim());
  const handleCreate = () => {
    if (!canContinue) return;
    onCreate({
      country: productCountry,
      name: name.trim(),
      vertical,
      byLang: {},
      whitelist: {
        cvps: [], attrs: [], personas: [], audiences: [],
      },
    });
  };
  let createTip = '';
  if (!canCreate) createTip = "You don't have permission to create product offerings.";
  else if (!canContinue) createTip = 'Choose a country, vertical and enter the tagged name';

  return (
    <Modal
      title="Create Product Offering"
      open={open}
      onCancel={onClose}
      width={520}
      className="cc-modal"
      footer={(
        <Flex justify="space-between" align="center" gap={12}>
          <Typography.Metadata type="secondary">You&apos;ll add content, languages &amp; links next, then send for approval.</Typography.Metadata>
          <Flex gap={8}>
            <Button onClick={onClose}>Cancel</Button>
            <Tooltip title={createTip}>
              <Button type="primary" disabled={!canContinue} onClick={handleCreate}>Continue to editor</Button>
            </Tooltip>
          </Flex>
        </Flex>
      )}
    >
      <div className="cc-field">
        <FieldLabel required>Country</FieldLabel>
        <Select disabled={!canCreate} value={productCountry} onChange={setProductCountry} className="cc-full-width" options={countries.map((c) => ({ value: c.id, label: c.name }))} />
      </div>
      <div className="cc-field">
        <FieldLabel required>Vertical</FieldLabel>
        <Select disabled={!canCreate} value={vertical} onChange={setVertical} className="cc-full-width" options={verticals.map((v) => ({ value: v.id, label: v.name }))} />
      </div>
      <div className="cc-field">
        <FieldLabel required>Tagged Name</FieldLabel>
        <Input
          disabled={!canCreate}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={() => { if (canContinue) handleCreate(); }}
          placeholder="Tagged name..."
        />
      </div>
    </Modal>
  );
};
